import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import { fetchSsmlById } from '@editor/store/slices/ssml.slice'
import {
  EditorContext,
  EditorContextType,
} from '@editor/components/editorContext'
import {
  fetchAllTags,
  TagAttribute,
  TagModel,
} from '@editor/store/slices/tag.slice'
import {Button, ButtonGroup, Dropdown, Spinner} from 'react-bootstrap'
import { useHistory } from '@editor/components/misc/historyController'
import { FaRedo, FaUndo } from 'react-icons/fa'
import { useTheme } from '@config/themeContext'
import { toast } from 'react-toastify'
import { uploadAudio } from '@editor/store/thunks/audio.thunk'
import { AudioModel } from '@editor/store/slices/audio.slice'
import { unwrapResult } from '@reduxjs/toolkit'
import {getJobLoading} from '@editor/store/selectors/job.selector';
import {useSelector} from 'react-redux';

const EditorForm: FC = () => {
  const {
    dispatch,
    setSelectedRange,
    editorRef,
    formMethods,
    job,
    tags,
    setGlobalTags,
    selectedRange,
    setSelectedPathTags,
    ssml,
  } = useContext(EditorContext) as EditorContextType
  const { recordChange, undo, redo, clearHistory, canUndo, canRedo } =
    useHistory()
  const { setValue, register, getValues } = formMethods
  const [localSelection, setLocalSelection] = useState<Range | null>()
  const [xPosition, setXPosition] = useState(0)
  const [yPosition, setYPosition] = useState(0)
  const [showMenu, setShowMenu] = useState(false)
  const [showAudioMenu, setShowAudioMenu] = useState(false)
  const [audioUploading, setAudioUploading] = useState(false)
  const { theme } = useTheme()

  const jobLoading = useSelector(getJobLoading)

  useEffect(() => {
    if (tags.length === 0) {
      dispatch(fetchAllTags())
    }
  }, [dispatch])

  useEffect(() => {
    if (job) {
      dispatch(fetchSsmlById(job.ssml)).then(clearHistory)
    }
  }, [job, dispatch])

  useEffect(() => {
    register('editorArea')
  }, [register])

  useEffect(() => {
    if (ssml) {
      const editorElement = editorRef.current
      if (editorElement) {
        editorElement.innerHTML = ssml.ssmlContent
      }
    }
    handleEditorContentChange()
  }, [ssml])

  useEffect(() => {
    computeAndSetTagPathForElement(selectedRange)
    handleEditorContentChange()
  }, [selectedRange])

  const onComputeRangeClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (localSelection) {
      setSelectedPathTags([])
      setSelectedRange(localSelection)
      setShowMenu(false)
      setShowAudioMenu(false)
    }
  }

  const onAddAudioClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setShowAudioMenu(true)
    setShowMenu(false)
  }

  const onAudioUploadError = (error: any) => {
    toast.error('Audio se nepodařilo nahrát:', error)
  }

  const onAudioUploadSuccess = (audio: AudioModel) => {
    toast.success('Audio nahráno')
    const audioPlaceholder = document.createElement('audio_placeholder')
    audioPlaceholder.setAttribute('data-audio-name', audio.audioName)

    const audioElement = document.createElement('audio')
    audioElement.setAttribute('preload', 'none')
    audioElement.setAttribute('src', audio.audioUrl)

    audioPlaceholder.appendChild(audioElement)
    if (localSelection) {
      insertNewMediaTagOnOffset(localSelection, audioPlaceholder)
    }
  }

  function encloseElementInSpeakVoice(element: HTMLElement) {
    const speak = document.createElement('speak')
    const voice = document.createElement('voice')
    if (job) voice.setAttribute('name', job.usedVoiceName)
    voice.appendChild(element)
    speak.appendChild(voice)
    return speak
  }


  const handleAudioUpload = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const form = e.currentTarget
    const audioName = form.elements.namedItem('audioName') as HTMLInputElement
    const audioFile = form.elements.namedItem('audioFile') as HTMLInputElement

    if (!audioName) {
      toast.warn('Nebylo zadáno jméno audia, bude použito jméno souboru')
    }
    if (!audioFile || !audioFile.files || audioFile.files.length != 1) {
      toast.error('Chybí soubor k uploadu')
    } else {
      setAudioUploading(true)
      toast.info('Nahrávám audio...')
      dispatch(uploadAudio({ file: audioFile.files[0], name: audioName.value }))
        .then(unwrapResult)
        .then(onAudioUploadSuccess)
        .catch(onAudioUploadError)
        .finally(() => {
          setShowAudioMenu(false)
          setAudioUploading(false)
        })
    }
  }

  const onAddMediaTagClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setShowMenu(false)
    setShowAudioMenu(false)
    if (localSelection) {
      insertNewMediaTagOnOffset(localSelection)
    }
  }

  const insertNewMediaTagOnOffset = (
    range: Range,
    content: HTMLElement | null = null
  ) => {
    if (!editorRef.current) return

    const element = findTopmostElement(range.commonAncestorContainer)

    const parParent = findEnclosingElementByName(element, 'par')
    const mediaParent = findEnclosingElementByName(element, 'media')

    const parent = element.parentNode
    if (!parent || !mediaParent || !parParent) {
      console.error(
        'Cannot add new media as the selected element has no parent or no media or par parent'
      )
      return
    }

    const [nodeBefore, nodeAfter] = splitElementOnElement(
      mediaParent as HTMLElement,
      element as HTMLElement
    )
    if (!nodeBefore || !nodeAfter) {
      console.error('Unable to parse nodes before and after')
      return
    }

    let contentBefore: HTMLElement | null = null
    let contentAfter: HTMLElement | null = null

    if (range.startOffset == 0) {
      contentAfter = element
    } else if (range.startOffset == element.textContent?.length) {
      contentBefore = element
    } else {
      ;[contentBefore, contentAfter] = splitElementOnOffset(
        element,
        range.startOffset
      )
    }
    assembleAndInsertNewMediaTagWithContents(
      content,
      parent,
      parParent,
      mediaParent,
      nodeBefore,
      nodeAfter,
      contentBefore,
      contentAfter
    )
  }

  const assembleAndInsertNewMediaTagWithContents = (
    content: HTMLElement | null,
    parent: ParentNode,
    parParent: Node,
    mediaParent: Node,
    nodeBefore: HTMLElement,
    nodeAfter: HTMLElement,
    contentBefore: HTMLElement | null,
    contentAfter: HTMLElement | null
  ) => {
    if (contentBefore) {
      if (nodeBefore.tagName.toLowerCase() == 'media') {
        contentBefore = encloseElementInSpeakVoice(contentBefore)
      }
      nodeBefore.appendChild(contentBefore)
    }

    if (contentAfter) {
      if (nodeAfter.tagName.toLowerCase() == 'media') {
        contentAfter = encloseElementInSpeakVoice(contentAfter)
      }
      nodeAfter.insertBefore(contentAfter, nodeAfter.firstChild)
    }

    const newMedia = document.createElement('media')
    // Add begin attribute to be included and substituted with correct id on save
    newMedia.setAttribute('begin', 'mediaXX.end-0.000s')

    if (content) {
      newMedia.appendChild(content)
    } else {
      const newContent = encloseElementInSpeakVoice(document.createElement('p'))
      newMedia.appendChild(newContent)
    }

    parParent.insertBefore(nodeBefore, mediaParent)
    parParent.insertBefore(newMedia, mediaParent)
    parParent.replaceChild(nodeAfter, mediaParent)

    const newRange = document.createRange()
    newRange.selectNodeContents(parent)
    computeAndSetTagPathForElement(newRange)
    handleEditorContentChange()
  }

  const findEnclosingElementByName = (
    element: Node,
    searchedTagName: string
  ) => {
    let currentElement = element
    while (currentElement.parentElement) {
      currentElement = currentElement.parentElement
      if (
        currentElement.nodeName &&
        currentElement.nodeName.toLowerCase() == searchedTagName
      ) {
        return currentElement
      }
    }
  }

  const splitElementOnElement = (
    element: HTMLElement,
    splitNode: HTMLElement
  ) => {
    if (!element.contains(splitNode)) {
      console.error('Cannot split element by node that is not a descendant')
      return [element, null]
    }

    const firstPart = element.cloneNode(false) as HTMLElement
    const secondPart = element.cloneNode(false) as HTMLElement

    splitElementTreeOnElementRecursively(
      element,
      splitNode,
      firstPart,
      secondPart
    )

    return [firstPart, secondPart]
  }

  const splitElementTreeOnElementRecursively = (
    currentElement: HTMLElement,
    targetNode: HTMLElement,
    firstPartParent: HTMLElement,
    secondPartParent: HTMLElement
  ) => {
    let found = false
    currentElement.childNodes.forEach((child) => {
      if (child == targetNode) {
        found = true
        return
      }
      if (found) {
        secondPartParent.appendChild(child.cloneNode(true))
      } else if (child.contains(targetNode)) {
        const [firstPart, secondPart] = splitElementTreeOnElementRecursively(
          child as HTMLElement,
          targetNode,
          child.cloneNode(false) as HTMLElement,
          child.cloneNode(false) as HTMLElement
        )
        found = true
        if (firstPart) {
          firstPartParent.appendChild(firstPart)
        }
        if (secondPart) {
          secondPartParent.appendChild(secondPart)
        }
      } else {
        firstPartParent.appendChild(child.cloneNode(true))
      }
    })

    return [firstPartParent, secondPartParent]
  }

  // Splits an element into two parts with same tag name and attributes by text content index
  const splitElementOnOffset = (element: HTMLElement, splitPoint: number) => {
    const parent = element.parentElement
    if (element.textContent && parent) {
      const beforeSplit = document.createElement(element.tagName)
      const afterSplit = document.createElement(element.tagName)

      Array.from(element.attributes).forEach((attr) => {
        beforeSplit.setAttribute(attr.name, attr.value)
        afterSplit.setAttribute(attr.name, attr.value)
      })

      beforeSplit.textContent = element.textContent.slice(0, splitPoint)
      afterSplit.textContent = element.textContent.slice(splitPoint)

      return [beforeSplit, afterSplit]
    }
    return [element, null]
  }

  const handleEditorContentChange = () => {
    const editorElement = editorRef.current
    if (editorElement) {
      setValue('editorArea', editorElement.innerHTML)
      computeGlobalTagPath()
      recordChange(editorElement.innerHTML)
    }
  }

  // Little unclear naming, actually finds first parent that is HTMLElement (not text)
  const findTopmostElement = (commonAncestorContainer: Node) => {
    let topmostElement = commonAncestorContainer

    while (topmostElement && !(topmostElement instanceof HTMLElement)) {
      if (topmostElement.parentNode) topmostElement = topmostElement.parentNode
    }
    return topmostElement
  }

  const computeAndSetTagPathForElement = (range: Range | null) => {
    if (range) {
      const topmostElement = findTopmostElement(range.commonAncestorContainer)

      if (topmostElement) {
        const path = computeSingleTagPath(topmostElement)
        setSelectedPathTags(path)
      }
    }
  }

  const setSelectedToLocalSelection = () => {
    const selection = window.getSelection()
    if (selection) {
      const range = selection.getRangeAt(0)
      setLocalSelection(range.cloneRange())
    }
  }

  // Creating a lookup map for faster execution
  const tagDictionary: Record<string, TagModel> = {}
  tags.forEach((tag: TagModel) => {
    tagDictionary[tag.tagName] = tag
  })

  // Extraction of TagAttribute from Attr
  const findStoreAttributes = (storeTag: TagModel, attributes: NamedNodeMap) =>
    Array.from(attributes)
      .map((attr: Attr) => {
        const storeAttr = storeTag.tagAttributes.find(
          (attribute: TagAttribute) => attribute.attributeName === attr.name
        )
        return storeAttr ? { ...storeAttr, attributeValue: attr.value } : null
      })
      .filter(
        (attr: TagAttribute | null): attr is TagAttribute => attr !== null
      )

  // Creation of the TagModel object with attributes sibling
  const computeTagModel = (
    storeTag: TagModel,
    tagAttributes: TagAttribute[]
  ) => ({
    ...storeTag,
    tagAttributes: tagAttributes,
  })

  // Ensures that only one Speak tag will be present in the path
  const ensureOnlyOneSpeakInPath = (path: TagModel[]): TagModel[] => {
    let speakFound = false
    return path.filter((tag) => {
      if (tag.tagName.toLowerCase() === 'speak') {
        if (speakFound) return false
        speakFound = true
      }
      return true
    })
  }

  // Main function to compute the TagPath
  const computeSingleTagPath = (element: HTMLElement): TagModel[] => {
    let path: TagModel[] = []
    for (
      let currentElement: HTMLElement | null = element;
      currentElement && currentElement !== editorRef.current;
      currentElement = currentElement.parentNode as HTMLElement
    ) {
      const tagName = currentElement?.tagName.toLowerCase()
      const storeTag = tagDictionary[tagName]
      if (!storeTag) continue
      const tagAttributes = findStoreAttributes(
        storeTag,
        currentElement.attributes
      )
      path.unshift(computeTagModel(storeTag, tagAttributes))
    }

    path = ensureOnlyOneSpeakInPath(path)
    return path
  }

  // Filters out non-text nodes
  const retrieveNonTextChildren = (childNodes: NodeListOf<ChildNode>) => {
    return Array.from(childNodes).filter(
      (child) => child.nodeType === Node.ELEMENT_NODE
    ) as HTMLElement[]
  }

  // Navigates through element, computes and sets tag path
  // for the first element with multiple child nodes
  const traverseElementChildren = (element: ChildNode | null) => {
    while (element) {
      if (element.nodeType === Node.ELEMENT_NODE) {
        const nonTextChildren = retrieveNonTextChildren(element.childNodes)
        if (nonTextChildren.length !== 1) {
          const path = computeSingleTagPath(element as HTMLElement)
          setGlobalTags(path)
          break
        } else {
          element = nonTextChildren[0]
        }
      } else {
        element = element.nextSibling
      }
    }
  }

  const computeGlobalTagPath = (): void => {
    if (editorRef.current) {
      const currentElement: ChildNode | null = editorRef.current.firstChild
      traverseElementChildren(currentElement)
    }
  }

  const handleRightClick = (event: React.MouseEvent) => {
    event.preventDefault()
    const bodyRect = document.body.getBoundingClientRect()
    const elemRect = event.currentTarget.getBoundingClientRect()
    const offsetX = elemRect.left / 1.04 - bodyRect.left
    const offsetY = elemRect.top / 1.05 - bodyRect.top
    setXPosition(event.clientX - offsetX)
    setYPosition(event.clientY - offsetY)
    setShowMenu(true)
    setSelectedToLocalSelection()
  }

  const handleClick = () => {
    if (!audioUploading) {
      setShowMenu(false)
      setShowAudioMenu(false)
    }
  }

  const handleUndo = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault()
    const previousState = undo()
    if (previousState !== null) {
      const editorElement = editorRef.current
      if (editorElement) {
        if (typeof previousState === 'string') {
          editorElement.innerHTML = previousState
          setValue('editorArea', previousState)
        }
      }
    }
  }

  const handleRedo = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault()
    const nextState = redo()
    if (nextState !== null) {
      const editorElement = editorRef.current
      if (editorElement) {
        if (typeof nextState === 'string') {
          editorElement.innerHTML = nextState
          setValue('editorArea', nextState)
        }
      }
    }
  }

  const buttonStyle = (canAction: boolean) => ({
    color: canAction ? (theme === 'dark' ? 'white' : 'black') : 'inherit',
    opacity: canAction ? 1 : 0.3,
  })

  return (
    <div style={{ position: 'relative' }}>
      <div // TODO move to inner component
        style={{
          position: 'absolute',
          zIndex: '100',
          maxWidth: 'fit-content',
          top: '-3.6rem',
          right: '1.5rem',
        }}
      >
        <ButtonGroup aria-label="Undo/Redo">
          <Button
            onClick={handleUndo}
            disabled={!canUndo()}
            variant="outline-secondary"
            style={buttonStyle(canUndo())}
          >
            <FaUndo />
          </Button>
          <Button
            onClick={handleRedo}
            disabled={!canRedo()}
            variant="outline-secondary"
            style={buttonStyle(canRedo())}
          >
            <FaRedo />
          </Button>
        </ButtonGroup>
      </div>

      <div
        id="editor"
        ref={editorRef}
        contentEditable
        onInput={handleEditorContentChange}
        onContextMenu={handleRightClick}
        onClick={handleClick}
        spellCheck={false}
        className="styled-custom-tags"
        style={{
          minHeight: '50vh',
          border: 'none',
          padding: '10px',
          maxHeight: '60vh',
          overflow: 'scroll',
          outline: 'none',
          position: 'relative',
        }}
      ></div>
      {showMenu && ( // TODO move to inner component
        <Dropdown
          style={{ position: 'absolute', top: yPosition, left: xPosition }}
          show
        >
          <Dropdown.Menu>
            {localSelection && localSelection.toString().length > 0 && (
              <Dropdown.Item onClick={onComputeRangeClick}>
                Vybrat úsek
              </Dropdown.Item>
            )}
            {localSelection && localSelection.toString().length == 0 && (
              <>
                <Dropdown.Item onClick={onAddMediaTagClick}>
                  Vložit nový media tag
                </Dropdown.Item>
                <Dropdown.Item onClick={onAddAudioClick}>
                  Vložit audio
                </Dropdown.Item>
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
      )}
      {showAudioMenu && ( // TODO move to inner component
        <form // TODO move to inner component
          className="audio-upload-form"
          style={{ position: 'absolute', top: yPosition, left: xPosition }}
          onSubmit={handleAudioUpload}
        >
          {!audioUploading && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '10px',
              }}
            >
              <input
                type="text"
                name="audioName"
                placeholder="Jméno audia"
                style={{ marginBottom: '10px', padding: '5px' }}
              />
              <input
                type="file"
                name="audioFile"
                style={{ marginBottom: '10px', padding: '5px' }}
              />
              <button className="btn btn-primary" type="submit">
                Vložit
              </button>
            </div>
          )}
          {audioUploading && (
            <div
              style={{
                minWidth: '364px',
                minHeight: '155px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            </div>
          )}
        </form>
      )}
    </div>
  )
}

export default EditorForm
